import { useEffect, useRef } from 'react';
import { readDraft, writeDraft, draftKey } from '@/services/idbDraft';

const DEBOUNCE_MS = 250;

/**
 * Autosaves a string value to IDB on every change (debounced) and restores it
 * once on mount. Used by the bottom-input textarea so accidental reloads don't
 * lose typed prompts.
 */
export function useDraft(
  episodeUuid: string | null,
  kind: 'scene' | 'batch',
  sceneId: string | null,
  value: string,
  onRestore: (restored: string) => void,
): void {
  const timer = useRef<number | null>(null);
  const restored = useRef(false);

  // Restore once when the key changes
  useEffect(() => {
    if (!episodeUuid) return;
    restored.current = false;
    const key = draftKey(episodeUuid, kind, sceneId ?? undefined);
    void readDraft(key).then((draft) => {
      if (!restored.current && draft && draft !== value) onRestore(draft);
      restored.current = true;
    });
    // Cleanup pending writes on unmount
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [episodeUuid, kind, sceneId, onRestore, value]);

  // Persist on change (debounced)
  useEffect(() => {
    if (!episodeUuid || !restored.current) return;
    if (timer.current !== null) window.clearTimeout(timer.current);
    const key = draftKey(episodeUuid, kind, sceneId ?? undefined);
    timer.current = window.setTimeout(() => {
      void writeDraft(key, value);
    }, DEBOUNCE_MS);
  }, [value, episodeUuid, kind, sceneId]);
}
